import { useMemo } from 'react';
import { Dividend, Operation, Partner } from 'types';

type UseGetDividendDataForPartnerProps = {
  dividendOperation: Operation | undefined;
  partner: Partner;
};

const extractOldDividendData = (operation: Operation, partner: Partner) => {
  const dividend = operation?.dividends?.find(
    (dividend) =>
      ((dividend as Dividend).partner as Partner)?._id === partner._id
  ) as Dividend | undefined;

  return {
    ndPercentage: dividend?.percent ?? 0,
    retentionPercentage: dividend?.retention ?? 0,
    amount: dividend?.amount ?? 0,
    retentionAmount: (dividend?.grossAmount ?? 0) - (dividend?.amount ?? 0),
  };
};

const extractNewDividendData = (operation: Operation, partner: Partner) => {
  const dividend = partner.dividends?.find(
    (d) => d.operation === operation?._id
  );

  const operationDividend = operation?.dividend;
  const customRetentions = operationDividend?.customRetentions || [];

  const existingIndex = customRetentions.findIndex(
    (retention) => retention.partner === partner._id
  );

  const retentionPercentage =
    existingIndex !== -1 && existingIndex !== undefined
      ? customRetentions[existingIndex].retentionPercentage
      : operationDividend?.retentionPercentage ?? 0;

  return {
    ndPercentage: dividend?.ndPercentage ?? 0,
    retentionPercentage,
    amount: dividend?.amount ?? 0,
    retentionAmount: dividend?.retentionAmount ?? 0,
  };
};

export const useGetDividendDataForPartner = ({
  dividendOperation,
  partner,
}: UseGetDividendDataForPartnerProps) => {
  const data = useMemo(() => {
    if (!dividendOperation) {
      return {
        ndPercentage: 0,
        retentionPercentage: 0,
        amount: 0,
        retentionAmount: 0,
      };
    }
    const hasOldDividends = !!dividendOperation?.dividends?.length;

    return hasOldDividends
      ? extractOldDividendData(dividendOperation, partner)
      : extractNewDividendData(dividendOperation, partner);
  }, [dividendOperation, partner]);

  return { data };
};
